/**
 * OperationModeSelector - Selector de modo de operación tri-estado
 * 
 * Modos:
 * - LOCAL: Usa programaciones locales del dispositivo
 * - REMOTO: Recibe eventos programados desde la plataforma
 * - MANUAL: Solo acepta comandos manuales desde la plataforma
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  Radio, 
  Cloud, 
  Hand,
  Loader2 
} from 'lucide-react';
import type { ControllerOperationMode } from '@/types/api';

interface OperationModeSelectorProps {
  currentMode: ControllerOperationMode;
  onModeChange: (mode: ControllerOperationMode) => void;
  isLoading?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const modeConfig: Record<ControllerOperationMode, {
  label: string;
  shortLabel: string;
  description: string;
  icon: typeof Radio;
  color: string;
  bgColor: string;
  borderColor: string;
}> = {
  local: {
    label: 'Local',
    shortLabel: 'LOC',
    description: 'Usa programaciones locales del dispositivo',
    icon: Radio,
    color: 'text-amber-600',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/50',
  },
  remote: {
    label: 'Remoto',
    shortLabel: 'REM',
    description: 'Recibe programaciones desde la plataforma',
    icon: Cloud,
    color: 'text-blue-600',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/50',
  },
  manual: {
    label: 'Manual',
    shortLabel: 'MAN',
    description: 'Solo acepta comandos manuales desde la plataforma',
    icon: Hand,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/50',
  },
};

const modes: ControllerOperationMode[] = ['local', 'remote', 'manual'];

export function OperationModeSelector({
  currentMode,
  onModeChange,
  isLoading = false,
  disabled = false,
  size = 'md',
}: OperationModeSelectorProps) {
  const [hoveredMode, setHoveredMode] = useState<ControllerOperationMode | null>(null);

  const sizeClasses = {
    sm: 'h-7 text-xs',
    md: 'h-8 text-sm',
    lg: 'h-9 text-sm',
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
    lg: 'w-4 h-4',
  };

  return (
    <div className="inline-flex rounded-lg border border-border bg-muted/30 p-0.5">
      {modes.map((mode) => {
        const config = modeConfig[mode];
        const Icon = config.icon;
        const isActive = currentMode === mode;
        const isHovered = hoveredMode === mode;

        return (
          <Tooltip key={mode}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => !disabled && !isLoading && onModeChange(mode)}
                onMouseEnter={() => setHoveredMode(mode)}
                onMouseLeave={() => setHoveredMode(null)}
                disabled={disabled || isLoading}
                className={cn(
                  'relative flex items-center justify-center gap-1.5 rounded-md px-2.5 transition-all duration-200',
                  sizeClasses[size],
                  isActive && [
                    config.bgColor,
                    config.borderColor,
                    config.color,
                    'border shadow-sm font-medium',
                  ],
                  !isActive && [
                    'text-muted-foreground hover:text-foreground',
                    isHovered && 'bg-muted',
                  ],
                  (disabled || isLoading) && 'opacity-50 cursor-not-allowed',
                )}
              >
                {isLoading && isActive ? (
                  <Loader2 className={cn(iconSizes[size], 'animate-spin')} />
                ) : (
                  <Icon className={iconSizes[size]} />
                )}
                <span className="hidden sm:inline">{config.shortLabel}</span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[200px]">
              <p className="font-medium">{config.label}</p>
              <p className="text-xs text-muted-foreground">{config.description}</p>
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

export default OperationModeSelector;
